import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, Button } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api/client";
import dayjs from "dayjs";
import SecurityIcon from "@mui/icons-material/Security";
import EditIcon from '@mui/icons-material/Edit';
import { getLabelByKey, useChangeableItems } from "store/useDynamicLabel";

const ViewSuperAdmin = () => {
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const location = useLocation();
  const { adminID } = location?.state || "";
  const [overAllLoading, setOverAllLoading] = useState(false);
  const [superAdminData, setSuperAdminData] = useState(false);
  const navigate = useNavigate();
  const fetchById = async () => {
    setOverAllLoading(true);
    try {
      const res = await api.post("ViewSuperAdminByID", {
        user_id: adminID,
      });
      if (res?.data?.status === "success") {
        setSuperAdminData(res?.data?.data);
        setOverAllLoading(false);
      } else {
        setOverAllLoading(false);
      }
    } catch (error) {
      console.error("Error fetching admin by ID:", error);
      setOverAllLoading(false);
    } finally {
      setOverAllLoading(false);
    }
  };

  useEffect(() => {
    fetchById();
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      {/* Back Button */}
      <Grid
        item
        sx={{
          display: "flex",
          alignItems: "center",
          color: "#1c97e3",
          gap: "4px",
          cursor: "pointer",
          width: "fit-content",
          mb: 2,
        }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            window.history.back();
          }
        }}
        onClick={() => window.history.back()}
      >
        <ArrowBackIcon sx={{ fontSize: "17px" }} />
        <Typography
          sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}
        >
          Back
        </Typography>
      </Grid>

      {/* View Card */}
      <Box>
        {/* Header */}
        <Grid
          sx={{
            display: "flex",
            justifyContent: "space-between",
            borderBottom: "1px solid #d1d1d1",
          }}
        >
          <Grid
            sx={{
              mb: 2,
              display: "flex",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Grid
              sx={{
                backgroundColor: "#1c97e3",
                p: 0.5,
                borderRadius: "10px",
              }}
            >
              <SecurityIcon sx={{ color: "white", fontSize: "35px" }} />
            </Grid>
            <Grid>
              <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                {getLabel("SUPER_ADMIN")} Details
              </Typography>
            </Grid>
          </Grid>
          <Grid>
            <Button
              variant="contained"
              className="gradient-button"
              startIcon={<EditIcon />}
              onClick={() =>
                navigate("/addSuperAdmin", {
                  state: {
                    adminID: adminID,
                  },
                })
              }
            >
              Edit
            </Button>
          </Grid>
        </Grid>

        {/* Student Details */}
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", rowGap: 3 }}>
          {/* Basic Info */}
          <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GroupIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
            <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
              Basic Information
            </Typography>
          </Grid>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: 2,
              width: "100%",
            }}
          >
            <Grid
              sx={{
                display: "flex",
                flex: "row",
              }}
            >
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">First Name</Typography>
                <Typography>{superAdminData.first_name}</Typography>
              </Grid>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Last Name</Typography>
                <Typography>{superAdminData.last_name}</Typography>
              </Grid>
            </Grid>
            <Grid
              sx={{
                display: "flex",
                flex: "row",
              }}
            >
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Gender</Typography>
                <Typography>{superAdminData.gender}</Typography>
              </Grid>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Date of Birth</Typography>
                <Typography>{superAdminData.dob}</Typography>
              </Grid>
            </Grid>
            <Grid
              sx={{
                display: "flex",
                flex: "row",
              }}
            >
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Created By</Typography>
                <Typography>{superAdminData.created_by || "-"}</Typography>
              </Grid>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Created Date</Typography>
                <Typography>
                  {dayjs(superAdminData.created_at).format("DD-MM-YYYY")}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              sx={{
                display: "flex",
                flex: "row",
              }}
            >
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Updated By</Typography>
                <Typography>{superAdminData.updated_by || "-"}</Typography>
              </Grid>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Updated Date</Typography>
                <Typography>
                  {dayjs(superAdminData.updated_at).format("DD-MM-YYYY")}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              sx={{
                display: "flex",
                flex: "row",
              }}
            >
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Status</Typography>
                <Typography>{superAdminData.status}</Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Contact Info */}
          <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <MailOutlineIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
            <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
              Contact Information
            </Typography>
          </Grid>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: 2,
            }}
          >
            <Grid sx={{ display: "flex" }}>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Contact Number</Typography>
                <Typography>{superAdminData.phone}</Typography>
              </Grid>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Email</Typography>
                <Typography>{superAdminData.email}</Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Institute Info
          <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BusinessIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
            <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
              Institute Information
            </Typography>
          </Grid> */}
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: 2,
            }}
          >
            <Grid sx={{ display: "flex" }}>
              <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">{`${getLabel('INSTITUTE')} Name`}</Typography>
                <Typography>{superAdminData.institute_Name}</Typography>
              </Grid>
              {/* <Grid item xs={6} sx={{ width: "50%" }}>
                <Typography fontWeight="600">Assigned Trainers</Typography>
                <Typography>
                  {superAdminData.assignedTrainers.join(", ")}
                </Typography>
              </Grid> */}
            </Grid>
          </Grid>

          {/* Address Info */}
          <Grid sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnIcon sx={{ color: "#1c97e3", fontSize: "30px" }} />
            <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
              Address Information
            </Typography>
          </Grid>
          <Grid
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: 2,
            }}
          >
            <Grid item xs={12}>
              <Typography fontWeight="600">Address</Typography>
              <Typography>{superAdminData.address}</Typography>
            </Grid>
            <Grid sx={{ display: "flex", justifyContent: "space-between" }}>
              <Grid item xs={4}>
                <Typography fontWeight="600">City</Typography>
                <Typography>{superAdminData.city}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography fontWeight="600">State</Typography>
                <Typography>{superAdminData.state}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography fontWeight="600">Pincode</Typography>
                <Typography>{superAdminData.pincode}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default ViewSuperAdmin;
